import { useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'reactstrap';

interface Props {
  images: string[];
  onDelete: (image: string) => void;
  onSort: (images: string[]) => void;
}

/**
 * Grid de miniaturas: clic para ver en grande, arrastrar para reordenar y
 * eliminar con confirmación. El orden es el de la galería pública.
 */
const PropertyGalleryGrid = ({ images, onDelete, onSort }: Props) => {
  const [ordered, setOrdered] = useState<string[]>(images);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  // índice arrastrado en un ref: el state se aplica con retraso (ver dragstart)
  const draggedRef = useRef<number | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<string | null>(null);

  // Solo se resincroniza si cambia el contenido; el padre crea un array nuevo
  // en cada render y pisaría el orden recién arrastrado.
  const imagesKey = images.join('|');
  useEffect(() => setOrdered(images), [imagesKey]);

  const move = (from: number, to: number) => {
    if (from === to) return;
    const next = [...ordered];
    next.splice(to, 0, next.splice(from, 1)[0]);
    setOrdered(next);
    onSort(next);
  };

  return (
    <>
      <div className="d-flex flex-wrap">
        {ordered.map((image, index) => (
          <div
            key={image}
            draggable
            onDragStart={(e) => {
              draggedRef.current = index;
              e.dataTransfer.effectAllowed = 'move';
              // Firefox exige datos para iniciar el arrastre
              e.dataTransfer.setData('text/plain', String(index));
              // cambiar estilos en el mismo tick cancela el arrastre en Chrome
              setTimeout(() => setDragIndex(index), 0);
            }}
            onDragEnter={(e) => {
              e.preventDefault();
              setOverIndex(index);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (draggedRef.current !== null) move(draggedRef.current, index);
              draggedRef.current = null;
              setDragIndex(null);
              setOverIndex(null);
            }}
            onDragEnd={() => {
              draggedRef.current = null;
              setDragIndex(null);
              setOverIndex(null);
            }}
            className="position-relative mr-3 mb-3 rounded border bg-secondary"
            style={{
              width: 150,
              height: 110,
              cursor: 'grab',
              opacity: dragIndex === index ? 0.4 : 1,
            }}
          >
            <img
              alt={`Foto ${index + 1}`}
              src={image}
              draggable={false}
              className="rounded"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onClick={() => setPreview(image)}
            />
            {overIndex === index &&
              dragIndex !== null &&
              dragIndex !== index && (
                <div
                  className="position-absolute bg-primary rounded"
                  style={{
                    top: 0,
                    bottom: 0,
                    width: 4,
                    // la foto cae después de esta si viene de atrás, antes si viene de adelante
                    ...(dragIndex < index ? { right: -10 } : { left: -10 }),
                  }}
                />
              )}
            <span
              className="badge badge-dark position-absolute"
              style={{ top: 6, left: 6 }}
            >
              {index + 1}
            </span>
            <button
              type="button"
              aria-label="Eliminar foto"
              title="Eliminar"
              className="btn btn-sm btn-danger position-absolute p-0 rounded-circle"
              style={{ top: 6, right: 6, width: 24, height: 24 }}
              onClick={() => setToDelete(image)}
            >
              <i className="fa-solid fa-trash-can" style={{ fontSize: 11 }} />
            </button>
          </div>
        ))}
      </div>

      <Modal
        size="lg"
        centered
        isOpen={!!preview}
        toggle={() => setPreview(null)}
      >
        <div className="modal-header border-0 pb-0">
          <button
            type="button"
            className="close"
            aria-label="Cerrar"
            onClick={() => setPreview(null)}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div className="modal-body text-center">
          {preview && <img alt="" src={preview} style={{ maxWidth: '100%' }} />}
        </div>
      </Modal>

      <Modal centered isOpen={!!toDelete} toggle={() => setToDelete(null)}>
        <div className="modal-body text-center">
          <h4 className="mb-2">¿Eliminar esta foto?</h4>
          <p className="text-sm mb-0" style={{ color: '#525f7f' }}>
            Dejará de mostrarse en la galería de la propiedad.
          </p>
        </div>
        <div className="modal-footer justify-content-center">
          <Button color="secondary" outline onClick={() => setToDelete(null)}>
            Cancelar
          </Button>
          <Button
            color="danger"
            onClick={() => {
              if (toDelete) onDelete(toDelete);
              setToDelete(null);
            }}
          >
            Sí, eliminar
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default PropertyGalleryGrid;
